import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CancelTaskResponse,
  ListTasksOptions,
  ListTasksResponse,
  Task,
  cancelTask as universalCancelTask,
  createTask as universalCreateTask,
  getTask as universalGetTask,
  listTasks as universalListTasks,
} from './data-autocms-v2-background-task-tasks.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data' };

export function createTask(httpClient: HttpClient): CreateTaskSignature {
  return (
    task: NonNullablePaths<
      Task,
      | `copyFieldDataOptions.dataCollectionId`
      | `copyFieldDataOptions.sourceFieldKey`
      | `copyFieldDataOptions.targetFieldKey`
      | `deleteByQueryOptions.dataCollectionId`
      | `editFieldDataOptions.dataCollectionId`
      | `editFieldDataOptions.fieldKey`
      | `type`
      | `updatePublishStatusOptions.dataCollectionId`
      | `updatePublishStatusOptions.operation`,
      3
    >
  ) =>
    universalCreateTask(
      task,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateTaskSignature {
  /**
   * Creates and runs a new background task.
   * @param - Task to create and run.
   * @returns Created task.
   */
  (
    task: NonNullablePaths<
      Task,
      | `copyFieldDataOptions.dataCollectionId`
      | `copyFieldDataOptions.sourceFieldKey`
      | `copyFieldDataOptions.targetFieldKey`
      | `deleteByQueryOptions.dataCollectionId`
      | `editFieldDataOptions.dataCollectionId`
      | `editFieldDataOptions.fieldKey`
      | `type`
      | `updatePublishStatusOptions.dataCollectionId`
      | `updatePublishStatusOptions.operation`,
      3
    >
  ): Promise<
    NonNullablePaths<
      Task,
      | `deleteByQueryOptions.dataCollectionId`
      | `deleteByQueryOptions.environment`
      | `deleteByQueryOptions.publishPluginOptions.includeDraftItems`
      | `copyFieldDataOptions.dataCollectionId`
      | `copyFieldDataOptions.environment`
      | `copyFieldDataOptions.sourceFieldKey`
      | `copyFieldDataOptions.targetFieldKey`
      | `editFieldDataOptions.dataCollectionId`
      | `editFieldDataOptions.environment`
      | `editFieldDataOptions.fieldKey`
      | `editFieldDataOptions.operations.addItems`
      | `editFieldDataOptions.operations.removeItems`
      | `editFieldDataOptions.operations.replaceItems`
      | `editFieldDataOptions.operations.removeDuplicates`
      | `updatePublishStatusOptions.dataCollectionId`
      | `updatePublishStatusOptions.environment`
      | `updatePublishStatusOptions.operation`
      | `_id`
      | `type`
      | `status`
      | `itemsFailed`
      | `failures`
      | `failures.${number}.code`
      | `failures.${number}.description`
      | `estimatedItemCount`
      | `itemsSucceeded`,
      4
    >
  >;
}

export function listTasks(httpClient: HttpClient): ListTasksSignature {
  return (options?: ListTasksOptions) =>
    universalListTasks(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListTasksSignature {
  /**
   * Lists all existing tasks.
   */
  (options?: ListTasksOptions): Promise<
    NonNullablePaths<
      ListTasksResponse,
      | `tasks`
      | `tasks.${number}.deleteByQueryOptions.dataCollectionId`
      | `tasks.${number}.deleteByQueryOptions.environment`
      | `tasks.${number}.deleteByQueryOptions.publishPluginOptions.includeDraftItems`
      | `tasks.${number}.copyFieldDataOptions.dataCollectionId`
      | `tasks.${number}.copyFieldDataOptions.environment`
      | `tasks.${number}.copyFieldDataOptions.sourceFieldKey`
      | `tasks.${number}.copyFieldDataOptions.targetFieldKey`
      | `tasks.${number}.editFieldDataOptions.dataCollectionId`
      | `tasks.${number}.editFieldDataOptions.environment`
      | `tasks.${number}.editFieldDataOptions.fieldKey`
      | `tasks.${number}.editFieldDataOptions.operations.removeDuplicates`
      | `tasks.${number}.updatePublishStatusOptions.dataCollectionId`
      | `tasks.${number}.updatePublishStatusOptions.environment`
      | `tasks.${number}.updatePublishStatusOptions.operation`
      | `tasks.${number}._id`
      | `tasks.${number}.type`
      | `tasks.${number}.status`
      | `tasks.${number}.itemsFailed`
      | `tasks.${number}.estimatedItemCount`
      | `tasks.${number}.itemsSucceeded`,
      6
    >
  >;
}

export function getTask(httpClient: HttpClient): GetTaskSignature {
  return (taskId: string) =>
    universalGetTask(
      taskId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTaskSignature {
  /**
   * Retrieves the specified task.
   * @param - Task ID.
   * @returns Retrieved task.
   */
  (taskId: string): Promise<
    NonNullablePaths<
      Task,
      | `deleteByQueryOptions.dataCollectionId`
      | `deleteByQueryOptions.environment`
      | `deleteByQueryOptions.publishPluginOptions.includeDraftItems`
      | `copyFieldDataOptions.dataCollectionId`
      | `copyFieldDataOptions.environment`
      | `copyFieldDataOptions.sourceFieldKey`
      | `copyFieldDataOptions.targetFieldKey`
      | `editFieldDataOptions.dataCollectionId`
      | `editFieldDataOptions.environment`
      | `editFieldDataOptions.fieldKey`
      | `editFieldDataOptions.operations.addItems`
      | `editFieldDataOptions.operations.removeItems`
      | `editFieldDataOptions.operations.replaceItems`
      | `editFieldDataOptions.operations.removeDuplicates`
      | `updatePublishStatusOptions.dataCollectionId`
      | `updatePublishStatusOptions.environment`
      | `updatePublishStatusOptions.operation`
      | `_id`
      | `type`
      | `status`
      | `itemsFailed`
      | `failures`
      | `failures.${number}.code`
      | `failures.${number}.description`
      | `estimatedItemCount`
      | `itemsSucceeded`,
      4
    >
  >;
}

export function cancelTask(httpClient: HttpClient): CancelTaskSignature {
  return (taskId: string) =>
    universalCancelTask(
      taskId,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelTaskSignature {
  /**
   * Cancels an existing task.
   * @param - Task ID.
   */
  (taskId: string): Promise<
    NonNullablePaths<
      CancelTaskResponse,
      | `task.deleteByQueryOptions.dataCollectionId`
      | `task.deleteByQueryOptions.environment`
      | `task.deleteByQueryOptions.publishPluginOptions.includeDraftItems`
      | `task.copyFieldDataOptions.dataCollectionId`
      | `task.copyFieldDataOptions.environment`
      | `task.copyFieldDataOptions.sourceFieldKey`
      | `task.copyFieldDataOptions.targetFieldKey`
      | `task.editFieldDataOptions.dataCollectionId`
      | `task.editFieldDataOptions.environment`
      | `task.editFieldDataOptions.fieldKey`
      | `task.editFieldDataOptions.operations.addItems`
      | `task.editFieldDataOptions.operations.removeItems`
      | `task.editFieldDataOptions.operations.replaceItems`
      | `task.editFieldDataOptions.operations.removeDuplicates`
      | `task.updatePublishStatusOptions.dataCollectionId`
      | `task.updatePublishStatusOptions.environment`
      | `task.updatePublishStatusOptions.operation`
      | `task._id`
      | `task.type`
      | `task.status`
      | `task.itemsFailed`
      | `task.failures`
      | `task.failures.${number}.code`
      | `task.failures.${number}.description`
      | `task.estimatedItemCount`
      | `task.itemsSucceeded`,
      5
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  ApplicationError,
  CancelTaskRequest,
  CancelTaskResponse,
  CopyFieldDataOptions,
  CreateTaskRequest,
  CreateTaskResponse,
  Cursors,
  DeleteByQueryOptions,
  DomainEvent,
  DomainEventBodyOneOf,
  EditFieldDataOptions,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Environment,
  GetTaskRequest,
  GetTaskResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListTasksOptions,
  ListTasksRequest,
  ListTasksResponse,
  MessageEnvelope,
  Operation,
  Operations,
  Paging,
  PagingMetadataV2,
  PublishPluginOptions,
  Replace,
  RestoreInfo,
  ScheduleOperation,
  Status,
  Task,
  TaskOptionsOneOf,
  Type,
  UpdatePublishStatusOptions,
  UpdatePublishStatusOptionsOptionsOneOf,
  WebhookIdentityType,
} from './data-autocms-v2-background-task-tasks.universal.js';
